/**
 * Frontend password validation utilities for Flexibook
 * Matches backend validation requirements
 */

export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

/**
 * Validates password against security requirements
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export function validatePassword(password) {
  const errors = [];
  
  if (!password || typeof password !== 'string') {
    return {
      isValid: false,
      errors: ['Password is required']
    };
  }

  // Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }

  // Check for uppercase letters
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter (A-Z)');
  }

  // Check for lowercase letters
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter (a-z)');
  }

  // Check for numbers
  if (PASSWORD_REQUIREMENTS.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number (0-9)');
  }

  // Check for special characters
  if (PASSWORD_REQUIREMENTS.requireSpecialChars) {
    const specialCharPattern = new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);
    if (!specialCharPattern.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*)');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Calculates password strength score
 * @param {string} password - The password to analyze
 * @returns {Object} - Strength analysis with score and individual requirements met
 */
export function getPasswordStrength(password) {
  const requirements = {
    length: password && password.length >= PASSWORD_REQUIREMENTS.minLength,
    uppercase: password && /[A-Z]/.test(password),
    lowercase: password && /[a-z]/.test(password),
    numbers: password && /[0-9]/.test(password),
    special: password && new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)
  };

  const metRequirements = Object.values(requirements).filter(Boolean).length;
  const totalRequirements = Object.keys(requirements).length;
  
  let strength = 'weak';
  let score = metRequirements / totalRequirements;
  
  if (score === 1) {
    strength = 'strong';
  } else if (score >= 0.6) {
    strength = 'medium';
  }

  return {
    score,
    strength,
    requirements,
    metRequirements,
    totalRequirements
  };
}

/**
 * React Hook Form validation rules for password
 * @returns {Object} - Validation rules object for react-hook-form
 */
export function getPasswordValidationRules() {
  return {
    required: "Password is required",
    minLength: {
      value: PASSWORD_REQUIREMENTS.minLength,
      message: `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`,
    },
    validate: (value) => {
      const validation = validatePassword(value);
      return validation.isValid || validation.errors[0];
    }
  };
}